import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Hex } from 'viem'

import {
  MetadataTarget,
  StateChangeItem,
  TokenMetadataLookup,
} from '~/components/txn-display/state-change/types'
import { buildStateChangeItems } from '~/components/txn-display/state-change/utils/buildStateChangeItems'
import { getPastTxnStateChange } from '~/components/txn-display/state-change/utils/getPastTxnStateChange'
import { getTokenMetadataLookupKey } from '~/components/txn-display/state-change/utils/getTokenMetadataLookupKey'
import { QUERY_KEYS } from '~/constants/react-query'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { fetchPrices } from '~/hooks/useTokenPrices/fetchPrices'
import { IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'

export const usePastStateChanges = (transaction: {
  transactionHash?: Hex
  chainId?: number
  addressesToWatch?: IAddress[]
  transactionTime?: string
  additionalTokens?: MetadataTarget[]
}) => {
  const {
    data: combinedResult,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.TRANSACTION_STATE_CHANGE,
      transaction.transactionHash,
      transaction.chainId,
      transaction.addressesToWatch,
    ],
    enabled: Boolean(
      transaction?.transactionHash &&
      transaction?.chainId &&
      transaction?.addressesToWatch,
    ),
    queryFn: () => {
      if (
        !transaction.transactionHash ||
        !transaction.chainId ||
        !transaction.addressesToWatch
      )
        throw new Error('Transaction hash is required')

      return getPastTxnStateChange({
        transactionHash: transaction.transactionHash,
        chainId: transaction.chainId,
        addressesToWatch: transaction.addressesToWatch,
      })
    },
  })

  const tokens = useMemo(() => {
    const unique = new Map<string, MetadataTarget>()

    const addToken = (
      chainId: MetadataTarget['chainId'],
      address: IAddress,
    ) => {
      const key = getTokenMetadataLookupKey(chainId, address)
      if (!unique.has(key)) {
        unique.set(key, { chainId, address })
      }
    }

    combinedResult?.stateChanges.forEach((change) =>
      addToken(change.chainId, change.token),
    )
    combinedResult?.approvalChanges.forEach((change) =>
      addToken(change.chainId, change.token),
    )
    combinedResult?.nftStateChanges.forEach((change) =>
      addToken(change.chainId, change.token),
    )
    transaction.additionalTokens?.forEach((token) =>
      addToken(token.chainId, token.address),
    )

    return Array.from(unique.values())
  }, [combinedResult, transaction.additionalTokens])

  const tokenMetadataList = useMultipleTokensMetadata(tokens, {
    includePrice: true,
  })

  const tokensKey = useMemo(
    () =>
      tokens
        .map((token) => `${token.chainId}:${token.address}`)
        .sort()
        .join('|'),
    [tokens],
  )

  const { data: historicalPriceLookup } = useQuery<Record<string, number>>({
    queryKey: [
      QUERY_KEYS.HISTORICAL_TOKEN_PRICES,
      transaction.transactionHash,
      transaction.transactionTime,
      tokensKey,
    ],
    enabled:
      Boolean(transaction.transactionTime) &&
      Boolean(transaction.transactionHash) &&
      tokens.length > 0,
    queryFn: async () => {
      const prices = await fetchPrices(tokens, {
        transactionTime: transaction.transactionTime,
        transactionHash: transaction.transactionHash ?? undefined,
      })

      const lookup: Record<string, number> = {}
      prices.forEach(({ chainId, address, price }) => {
        const key = getTokenMetadataLookupKey(
          chainId,
          EvmAddress.format(address as IAddress),
        )
        lookup[key] = price
      })

      return lookup
    },
  })

  const metadataLookup = useMemo(() => {
    const lookup: TokenMetadataLookup = new Map()

    tokens.forEach((token, index) => {
      const metadata = tokenMetadataList[index]
      if (!metadata) return

      const key = getTokenMetadataLookupKey(token.chainId, token.address)
      const historicalPrice = historicalPriceLookup?.[key]
      lookup.set(
        key,
        historicalPrice !== undefined
          ? { ...metadata, price: historicalPrice }
          : metadata,
      )
    })

    return lookup
  }, [tokens, tokenMetadataList, historicalPriceLookup])

  const stateChangeItems = useMemo<StateChangeItem[]>(() => {
    if (!combinedResult) return []

    return buildStateChangeItems({
      combinedResult,
      metadataLookup,
    })
  }, [combinedResult, metadataLookup])

  return {
    stateChangeItems,
    metadataLookup,
    isLoading,
    isError,
  }
}
